import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type SearchPdpsV1PortfoliosPortfolioIdPdpsGetData,
  type SearchPdpsV1PortfoliosPortfolioIdPdpsGetError,
  type SearchPdpsV1PortfoliosPortfolioIdPdpsGetResponse,
  searchPdpsV1PortfoliosPortfolioIdPdpsGet,
} from '../../generated';

export type SearchPdpsQueryVariables = SearchPdpsV1PortfoliosPortfolioIdPdpsGetData['path'] &
  SearchPdpsV1PortfoliosPortfolioIdPdpsGetData['query'];

export type SearchPdpsQuery = {
  data: SearchPdpsV1PortfoliosPortfolioIdPdpsGetResponse;
  total_count: number;
};

/**
 * Extracts the total_count from the Vizit-Total-Count response header
 */
function extractTotalCount(response: Response): number {
  const totalCountHeader = response.headers.get('vizit-total-count');
  return totalCountHeader ? Number.parseInt(totalCountHeader, 10) : 0;
}

export const useSearchPdpsQuery = <TData = SearchPdpsQuery, TError = SearchPdpsV1PortfoliosPortfolioIdPdpsGetError>(
  {
    portfolio_id,
    limit,
    offset,
    sort_by,
    sort_direction,
    brand,
    product_category_id,
    listing_score_level,
    search_item_id,
    search_name,
    search_brand,
    search_product_category,
  }: SearchPdpsQueryVariables,
  options?: Omit<UseQueryOptions<SearchPdpsQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<SearchPdpsQuery, TError, TData>({
    queryKey: [
      'SearchPdps',
      {
        portfolio_id,
        limit,
        offset,
        sort_by,
        sort_direction,
        brand,
        product_category_id,
        listing_score_level,
        search_item_id,
        search_name,
        search_brand,
        search_product_category,
      },
    ],
    queryFn: async () => {
      const { data, response } = await searchPdpsV1PortfoliosPortfolioIdPdpsGet({
        path: { portfolio_id },
        query: {
          limit,
          offset,
          sort_by,
          sort_direction,
          brand,
          product_category_id,
          listing_score_level,
          search_item_id,
          search_name,
          search_brand,
          search_product_category,
        },
        throwOnError: true,
      });
      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    ...options,
  });
};

useSearchPdpsQuery.getKey = ({
  portfolio_id,
  limit,
  offset,
  sort_by,
  sort_direction,
  brand,
  product_category_id,
  listing_score_level,
  search_item_id,
  search_name,
  search_brand,
  search_product_category,
}: SearchPdpsQueryVariables) => [
  'SearchPdps',
  {
    portfolio_id,
    limit,
    offset,
    sort_by,
    sort_direction,
    brand,
    product_category_id,
    listing_score_level,
    search_item_id,
    search_name,
    search_brand,
    search_product_category,
  },
];

useSearchPdpsQuery.fetcher = (
  {
    portfolio_id,
    limit,
    offset,
    sort_by,
    sort_direction,
    brand,
    product_category_id,
    listing_score_level,
    search_item_id,
    search_name,
    search_brand,
    search_product_category,
  }: SearchPdpsQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data, response } = await searchPdpsV1PortfoliosPortfolioIdPdpsGet({
      path: { portfolio_id },
      query: {
        limit,
        offset,
        sort_by,
        sort_direction,
        brand,
        product_category_id,
        listing_score_level,
        search_item_id,
        search_name,
        search_brand,
        search_product_category,
      },
      throwOnError: true,
    });
    return {
      data: data ?? [],
      total_count: extractTotalCount(response),
    };
  };
};
